import PropTypes from 'prop-types';

import { ButtonV2, LoaderSkeleton, OverlayTriggerTooltip, PlusPlainIcon } from '@utilities';
import { getPromptPillTooltip, getPromptPillValue } from '@helpers';
import {
  getPromptPillIcon,
  transformPromptSelections,
} from '../../../../../../pages/Narratives/utilities/helpers.js';
import { PILL_KEYS } from '../../../../../../pages/Narratives/utilities/constants.js';

import styles from './_index.module.scss';

const getAnswers = (answers, prompt) => {
  if (prompt.groupedPrompts) {
    const groupedAnswers = [];
    prompt.groupedPrompts.forEach((groupedPrompt) => {
      const groupedPromptAnswers = answers?.[groupedPrompt.promptKey] || [];
      groupedAnswers.push(...groupedPromptAnswers);
    });
    return groupedAnswers;
  }
  return answers?.[prompt.promptKey];
};

const TitleSelectedPrompts = ({ answers, handleReprompt, isPublicShare, selectedPrompts }) => {
  let pillPrompts = selectedPrompts;
  if (answers?.subscribedChannels?.length > 0) {
    pillPrompts = [
      ...pillPrompts,
      {
        id: 'subscribedChannels',
        name: 'Subscribed Channels',
        promptKey: 'subscribedChannels',
      },
    ];
  }

  const pillCount = Math.max(1, PILL_KEYS.filter((key) => answers?.[key]?.length > 0).length);
  const renderedPrompts = pillPrompts.filter((prompt) => {
    const answer = getAnswers(answers, prompt);
    const hasAnswer = Array.isArray(answer) ? answer.length > 0 : Boolean(answer);
    return hasAnswer || prompt?.isRequired || prompt?.isRequiredForModal;
  });
  const emptyPrompts = selectedPrompts.filter((prompt) => {
    const answer = getAnswers(answers, prompt);
    const hasAnswer = Array.isArray(answer) ? answer.length > 0 : Boolean(answer);
    return !hasAnswer && !prompt?.isRequired && !prompt?.isRequiredForModal;
  });

  if (selectedPrompts.length === 0) {
    const loaderPills = [];
    for (let i = 0; i < pillCount; i++) {
      loaderPills.push(
        <LoaderSkeleton height={24} key={`loader-pill-${i}`} width={150}>
          <rect height="24px" rx="8" ry="8" width="150px" x="0" y="0" />
        </LoaderSkeleton>
      );
    }
    return <div className={styles['title-selected-prompts']}>{loaderPills}</div>;
  }

  const canReprompt = typeof handleReprompt === 'function';

  return (
    <div className={styles['title-selected-prompts']}>
      {renderedPrompts?.map((prompt, index) => {
        const answer = getAnswers(answers, prompt) || [];
        const transformedAnswer = transformPromptSelections({ answers: answer, prompt });
        const answerValues = transformedAnswer.flatMap((item) => item.values);
        const hasDateRangeStringPair =
          prompt.promptKey === 'date_range' &&
          answerValues.length === 2 &&
          answerValues.every((value) => typeof value === 'string');
        const pillAnswers = hasDateRangeStringPair
          ? { [prompt.id]: [{ date_range: answerValues }] }
          : { [prompt.id]: answerValues };
        const value =
          prompt.promptKey !== 'subscribedChannels'
            ? getPromptPillValue({
                answers: pillAnswers,
                prompt: { ...prompt, isPrefixIncluded: false },
              })
            : 'Subscribed Channels';
        const tooltip =
          prompt.id !== 'date_range'
            ? getPromptPillTooltip({
                answers: pillAnswers,
                hasTooltip: true,
                prompt,
              })
            : '';
        const icon = getPromptPillIcon({ promptKey: prompt.promptKey });
        return (
          <OverlayTriggerTooltip content={tooltip} isFullWidth key={`prompt-${index}`}>
            <ButtonV2
              className={styles['title-selected-prompt-pill']}
              disabled={isPublicShare}
              icon={isPublicShare ? null : icon}
              isRequired={value === prompt.emptyValue}
              key={`prompt-${index}`}
              onClick={
                canReprompt && prompt.promptKey !== 'subscribedChannels'
                  ? () => handleReprompt(prompt)
                  : null
              }
              size="small"
              variant="subtle"
            >
              {value}
            </ButtonV2>
          </OverlayTriggerTooltip>
        );
      })}
      {emptyPrompts.length > 0 && !isPublicShare && (
        <ButtonV2
          className={styles['title-selected-prompt-pill']}
          icon={<PlusPlainIcon />}
          onClick={canReprompt ? () => handleReprompt(emptyPrompts[0]) : null}
          size="small"
          variant="subtle"
        >
          Add Prompts
        </ButtonV2>
      )}
    </div>
  );
};

TitleSelectedPrompts.propTypes = {
  answers: PropTypes.object.isRequired,
  handleReprompt: PropTypes.func,
  isPublicShare: PropTypes.bool,
  selectedPrompts: PropTypes.array.isRequired,
};

export default TitleSelectedPrompts;
